import { useIntersectionObserver, useSyncedRef } from "@react-hookz/web";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { Checkbox } from "scmp-app/components/newsletter/newsletter-list/checkbox";
import { useNewsletterListContext } from "scmp-app/components/newsletter/newsletter-list/newsletter-category-item-list/contexts";
import type { newsletterCategoryItemListNewsletterCategory$key } from "scmp-app/queries/__generated__/newsletterCategoryItemListNewsletterCategory.graphql";

import { NewsletterItemCard } from "./newsletter-item-card";
import { Container, Header, SelectAllContainer, SelectAllLabel, Title } from "./styles";

export type Props = React.DetailsHTMLAttributes<HTMLDivElement> & {
  isStopIntersectionObserve: boolean;
  onIntersection: (newsletterCategoryId: string) => void;
  reference: newsletterCategoryItemListNewsletterCategory$key;
};

export const NewsletterCategoryItemList = forwardRef<HTMLDivElement, Props>(
  ({ isStopIntersectionObserve, onIntersection, reference: reference_ }, reference) => {
    const newsletterCategory = useFragment(
      graphql`
        fragment newsletterCategoryItemListNewsletterCategory on NewsletterCategory {
          entityId
          name
          newsletters(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
            edges {
              node {
                entityId
                ...newsletterItemCardNewsletter
              }
            }
          }
        }
      `,
      reference_,
    );
    const [isSelectedAll, setIsSelectedAll] = useState(false);

    const { checkedNewsletterIdsMap, setCheckedNewsletterIdsMap } = useNewsletterListContext();
    const checkedNewsletterIds = useMemo(
      () => checkedNewsletterIdsMap?.[newsletterCategory.entityId] ?? [],
      [checkedNewsletterIdsMap, newsletterCategory.entityId],
    );

    const handleSelectAll = () => {
      // Prevent the delay of the checkbox
      setIsSelectedAll(selectAll => !selectAll);
      setCheckedNewsletterIdsMap(map => ({
        ...map,
        [newsletterCategory.entityId]: isSelectedAll
          ? []
          : (newsletterCategory.newsletters?.edges.map(edge => edge.node.entityId) ?? []),
      }));
    };

    useEffect(() => {
      setIsSelectedAll(
        newsletterCategory.newsletters?.edges.length === checkedNewsletterIds.length,
      );
    }, [checkedNewsletterIds, newsletterCategory.newsletters?.edges.length]);

    const titleReference = useRef(null);
    const intersectionObserver = useIntersectionObserver(titleReference, {
      rootMargin: "-63px 0px -35%",
      threshold: [1],
    });

    const isStopIntersectionObserveReference = useSyncedRef(isStopIntersectionObserve);
    useEffect(() => {
      if (isStopIntersectionObserveReference.current || !intersectionObserver?.isIntersecting)
        return;
      onIntersection(newsletterCategory.entityId);
    }, [
      intersectionObserver?.isIntersecting,
      isStopIntersectionObserveReference,
      newsletterCategory.entityId,
      onIntersection,
    ]);

    return (
      <Container ref={reference}>
        <Header>
          <Title ref={titleReference}>{newsletterCategory.name}</Title>
          <SelectAllContainer onClick={handleSelectAll}>
            <SelectAllLabel>Select all</SelectAllLabel>
            <Checkbox $checked={isSelectedAll} />
          </SelectAllContainer>
        </Header>
        {newsletterCategory.newsletters?.edges.map((edge, index) => (
          <NewsletterItemCard
            isChecked={checkedNewsletterIds.includes(edge.node.entityId)}
            key={index}
            onCheck={() => {
              const nodeIndex = checkedNewsletterIds.indexOf(edge.node.entityId);
              setCheckedNewsletterIdsMap(map => {
                let newCheckedNewsletterIds = [...(map?.[newsletterCategory.entityId] ?? [])];
                if (nodeIndex === -1) {
                  newCheckedNewsletterIds = [...newCheckedNewsletterIds, edge.node.entityId];
                } else {
                  newCheckedNewsletterIds.splice(nodeIndex, 1);
                }
                return {
                  ...map,
                  [newsletterCategory.entityId]: newCheckedNewsletterIds,
                };
              });
            }}
            reference={edge.node}
          />
        ))}
      </Container>
    );
  },
);

NewsletterCategoryItemList.displayName = "NewsletterCategoryItemList";
